import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isOidcCallbackEnabled } from '../utils/authMethod';
import { clearOidcDebugStorage } from '../utils/clearOidcDebugStorage';
import { isHttpOnlyAuthSessionEnabled } from '../utils/authSession';
import { useAuth } from '../contexts/AuthContext';
import CenteredLoader from '../components/CenteredLoader';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const AuthCallback: React.FC = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { completeSsoLogin } = useAuth();

  const code = query.get('code') || '';
  const state = query.get('state') || '';
  const error = query.get('error') || '';

  const oidcAllowed = isOidcCallbackEnabled();

  const [exchangeStatus, setExchangeStatus] = React.useState<
    'idle' | 'exchanging' | 'success' | 'error' | 'saml_only'
  >(oidcAllowed ? 'idle' : 'saml_only');

  const exchangedKey = 'provysn-okta-exchanged-code';

  React.useEffect(() => {
    if (code) {
      sessionStorage.setItem('provysn-okta-auth-code', code);
      if (state) sessionStorage.setItem('provysn-okta-auth-state', state);
    }
  }, [code, state]);

  React.useEffect(() => {
    const run = async () => {
      if (!oidcAllowed) return;
      if (!code || error) return;
      if (exchangeStatus !== 'idle') return;
      if (sessionStorage.getItem(exchangedKey) === code) return;
      setExchangeStatus('exchanging');
      try {
        const clientId = process.env.REACT_APP_OKTA_CLIENT_ID || '';
        if (!clientId) throw new Error('Missing REACT_APP_OKTA_CLIENT_ID');
        sessionStorage.setItem(exchangedKey, code);
        const resp = await fetch('/api/v1/auth/okta/exchange', {
          method: 'POST',
          credentials: isHttpOnlyAuthSessionEnabled() ? 'include' : 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            redirect_uri: `${window.location.origin}/auth/callback`,
            client_id: clientId
          })
        });
        const json = await resp.json();
        if (!resp.ok || !json?.success) throw new Error(json?.error || `HTTP ${resp.status}`);

        const turbo = json?.data?.turboiam;
        if (!turbo?.user) throw new Error('Missing Provysn session in response');

        completeSsoLogin(turbo.token || '', turbo.user, 'oidc');
        clearOidcDebugStorage();
        setExchangeStatus('success');
        navigate('/', { replace: true });
      } catch {
        sessionStorage.removeItem(exchangedKey);
        setExchangeStatus('error');
        navigate('/login', { replace: true });
      }
    };
    run();
  }, [code, error, exchangeStatus, navigate, oidcAllowed]);

  return <CenteredLoader variant="full" />;
};
export default AuthCallback;
